import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import store from 'app/store';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment-timezone';

import Grid from '@mui/material/Grid';
import { Alert, Button, Dialog, Tooltip } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import Counters from './Counters';
import ChartRange from './ChartRange';
import ChartProgress from './charts-components/ChartProgress';
import ChartDelayed from './charts-components/ChartDelayed';
import ChartCompleted from './charts-components/ChartCompleted';
import NewDashboardView from './NewDashboardView';

import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';

import { constants, URLWOCLOUDFLARE } from '../../../constants';
import { useSocket } from 'app/SocketContext';

import Snackbar from '@mui/material/Snackbar';
import { injectReducer } from 'app/store';
import JobTable from './JobTable';
import { connect } from 'react-redux';
import AuthReducer from 'app/stores/loadz/redux/Auth';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { RevenueReport } from './components/RevenueReport';
import { hasPermission } from 'app/main/loadz/utils';
import ButtonLog from '../ButtonLog';
import QuickDriverEarnings from './components/QuickDriverEarnings';
import client from 'client';
import FuseLoading from '@fuse/core/FuseLoading';
import TicketPopup from '../Ticket_jobs/TicketPopup';
import { useTranslation } from 'react-i18next';
import { saveCachedJobTableData, invalidateCache } from './dashboardFetchCache';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const action = (
  <Fragment>
    <Button color='secondary' size='small'>
      UNDO
    </Button>
    <IconButton size='small' aria-label='close' color='inherit'>
      <CloseIcon fontSize='small' />
    </IconButton>
  </Fragment>
);

/** Misma lógica que `JobTable` / caché: conteo de jobs desde el payload de `get-for-dashboard` / `dashboard-all-tables`. */
function jobCountFromDashboardTablePayload(payload) {
  if (!payload) return 0;
  if (Array.isArray(payload.jobs)) return payload.jobs.length;
  if (Array.isArray(payload.results)) return payload.results.length;
  return 0;
}

function ticketCountFromDashboardTablePayload(payload) {
  if (!payload || payload.countTicket == null || payload.countTicket === '') return 0;
  const n = Number(payload.countTicket);
  return Number.isFinite(n) ? n : 0;
}

/** Comparación robusta (backend puede mandar número o string). */
function companiesMatch(a, b) {
  if (a == null || b == null) return false;
  return String(a).trim() === String(b).trim();
}

function payloadCompanyId(data) {
  if (!data || typeof data !== 'object') return null;
  return data.company ?? data.company_id ?? data.c_id ?? data.compacctnum ?? data.comp_acct;
}

function Dashboard({ delay, company, user }) {
  const { t } = useTranslation('navigation');
  const { socket } = useSocket();
  const compname = company?.compname;
  const comp_email = company?.comp_email;
  // Primitivos estables para que resize no dispare efectos en hijos (evitar peticiones al redimensionar)
  const comp_acct = useMemo(() => company?.compacctnum, [company?.compacctnum]);
  const jobTypes = useMemo(() => ({ progress: 0, completed: 1, delay: 2 }), []);
  const [showLoading, setShowLoading] = useState(!delay);
  const [expandedProgress, setExpandedProgress] = useState(false);
  const [expandedDelayed, setExpandedDelayed] = useState(false);
  const [expandedCompleted, setExpandedCompleted] = useState(false);

  const [counters, setCounters] = useState({});

  const [progress, setProgress] = useState(0);
  const [delayed, setDelayed] = useState(0);
  const [completed, setCompleted] = useState(0);

  const [qtyProgress, setQtyProgress] = useState(0);
  const [qtyDelayed, setQtyDelayed] = useState(0);
  const [qtyCompleted, setQtyCompleted] = useState(0);

  const [progressTickets, setProgressTickets] = useState(0);
  const [delayedTickets, setDelayedTickets] = useState(0);
  const [completedTickets, setCompletedTickets] = useState(0);

  const [isProgress, setIsProgress] = useState(false);
  const [isDelayed, setIsDelayed] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const [response, setResponse] = useState('');

  const dispatch = useDispatch();

  const refreshProgress = useRef(null);
  const refreshDelayed = useRef(null);
  const refreshCompleted = useRef(null);
  const refreshGraph = useRef(null);
  const refreshDispatchSection = useRef(null);
  /** Debounce 400 ms como en la implementación previa con `socket.io` en este archivo (solo tablas). */
  const refreshLegacyDebounceRef = useRef(null);

  const [openSnack, setOpenSnack] = useState(false);
  const [severity, setSeverity] = useState('');
  const [message, setMessage] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  /** Toggle vista; el socket dedicado de tiempo real solo aplica a la vista nueva. */
  const [isNewDashboardView, setIsNewDashboardView] = useState(true);

  // Mejora 2: Estado para fetch unificado de las 3 tablas
  const [allTablesData, setAllTablesData] = useState(null);
  const allTablesFetchRef = useRef(false);

  const fetchDashboardAllTables = useCallback(() => {
    if (!comp_acct) return Promise.resolve();
    return client
      .get(`/jobs/dashboard-all-tables/${comp_acct}`)
      .then((res) => {
        const data = res.data;
        setAllTablesData(data);
        if (data.progress) saveCachedJobTableData(comp_acct, 0, data.progress);
        if (data.completed) saveCachedJobTableData(comp_acct, 1, data.completed);
        if (data.delayed) saveCachedJobTableData(comp_acct, 2, data.delayed);
      })
      .catch((err) => console.error('Error fetching dashboard-all-tables:', err));
  }, [comp_acct]);

  useEffect(() => {
    allTablesFetchRef.current = false;
  }, [comp_acct]);

  useEffect(() => {
    if (!comp_acct || allTablesFetchRef.current) return;
    allTablesFetchRef.current = true;
    fetchDashboardAllTables();
  }, [comp_acct, fetchDashboardAllTables]);

  /**
   * Los contadores In-Progress / Completed / Delayed en New Dashboard (y Counters) deben reflejar
   * los tres totales a la vez — no solo el `jobType` activo en el `JobTable` (filtro "Latest").
   */
  useEffect(() => {
    if (!allTablesData) return;
    const { progress, completed, delayed } = allTablesData;
    setProgress(jobCountFromDashboardTablePayload(progress));
    setCompleted(jobCountFromDashboardTablePayload(completed));
    setDelayed(jobCountFromDashboardTablePayload(delayed));
    setProgressTickets(ticketCountFromDashboardTablePayload(progress));
    setCompletedTickets(ticketCountFromDashboardTablePayload(completed));
    setDelayedTickets(ticketCountFromDashboardTablePayload(delayed));
  }, [allTablesData]);

  /** New Dashboard: invalidar caché, `dashboard-all-tables`, tablas y gráfico (socket + botón Refresh). */
  const syncNewDashboardData = useCallback(async () => {
    if (!comp_acct) return;
    invalidateCache('all-tables', comp_acct);
    invalidateCache('for-dashboard', 0, comp_acct);
    invalidateCache('for-dashboard', 1, comp_acct);
    invalidateCache('for-dashboard', 2, comp_acct);
    await fetchDashboardAllTables();
    const fns = [
      refreshProgress.current,
      refreshDelayed.current,
      refreshCompleted.current,
      refreshGraph.current,
    ].filter(Boolean);
    await Promise.all(fns.map((fn) => fn()));
  }, [comp_acct, fetchDashboardAllTables]);

  /**
   * Vista nueva: `fromServer`, `dashboardRefresh`, `dashboardRefreshDispatch` (socket) + refresco manual.
   */
  useEffect(() => {
    if (!socket || !comp_acct || !isNewDashboardView) return;

    const debouncedSync = () => {
      if (refreshLegacyDebounceRef.current) clearTimeout(refreshLegacyDebounceRef.current);
      refreshLegacyDebounceRef.current = setTimeout(() => {
        refreshLegacyDebounceRef.current = null;
        void syncNewDashboardData();
      }, 400);
    };

    const handleDashboardRefreshDispatch = async (payload) => {
      const raw = payload && typeof payload === 'object' ? payload : {};
      const cid = payloadCompanyId(raw);
      if (cid != null && !companiesMatch(cid, comp_acct)) return;
      await refreshDispatchSection.current?.();
    };

    const handleDashboardRefresh = async (payload) => {
      const raw = payload && typeof payload === 'object' ? payload : {};
      const cid = payloadCompanyId(raw);
      if (cid != null && !companiesMatch(cid, comp_acct)) return;
      await syncNewDashboardData();
    };

    const fromServerHandler = (raw) => {
      try {
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!data || typeof data !== 'object') return;

        const ids = Array.isArray(data.company_ids) ? data.company_ids : [];
        const isBatchPayload = data.batch === true || ids.length > 0;

        if (isBatchPayload) {
          const affectsUs = ids.some((id) => companiesMatch(id, comp_acct));
          if (affectsUs) {
            debouncedSync();
            return;
          }
          if (data.batch === true && ids.length === 0) {
            const tc = payloadCompanyId(data);
            if (tc != null && companiesMatch(tc, comp_acct)) {
              debouncedSync();
            }
          }
          return;
        }

        const cid = payloadCompanyId(data);
        if (cid == null || !companiesMatch(cid, comp_acct)) return;

        const inProg = data.inProgress || data.in_progress;
        const tkLabel = data.tk_no != null ? data.tk_no : '';
        const msg = data.completed
          ? `${t('COMPLETED')}: ${tkLabel}`
          : inProg
            ? `${t('IN_PROGRESS')}: ${tkLabel}`
            : `${t('DELAYED')}: ${tkLabel}`;
        setMessage(msg);
        setSeverity(data.completed ? 'success' : inProg ? 'warning' : 'error');
        setOpenSnack(true);
        setTimeout(() => setOpenSnack(false), 20000);

        debouncedSync();
      } catch (err) {
        // ignore parse errors
      }
    };

    const attachListeners = () => {
      socket.off('fromServer', fromServerHandler);
      socket.on('fromServer', fromServerHandler);
      socket.off('dashboardRefresh', handleDashboardRefresh);
      socket.on('dashboardRefresh', handleDashboardRefresh);
      socket.off('dashboardRefreshDispatch', handleDashboardRefreshDispatch);
      socket.on('dashboardRefreshDispatch', handleDashboardRefreshDispatch);
    };

    attachListeners();
    socket.on('connect', attachListeners);

    return () => {
      if (refreshLegacyDebounceRef.current) clearTimeout(refreshLegacyDebounceRef.current);
      socket.off('connect', attachListeners);
      socket.off('fromServer', fromServerHandler);
      socket.off('dashboardRefresh', handleDashboardRefresh);
      socket.off('dashboardRefreshDispatch', handleDashboardRefreshDispatch);
    };
  }, [socket, comp_acct, isNewDashboardView, t, syncNewDashboardData]);

  const handleExpandClickProgress = () => {
    setExpandedProgress(!expandedProgress);
  };

  const handleExpandClickDelayed = () => {
    setExpandedDelayed(!expandedDelayed);
  };

  const handleExpandClickCompleted = () => {
    setExpandedCompleted(!expandedCompleted);
  };

  const goTo = (id) => {
    var elmntToView = document.getElementById(id);
    elmntToView.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCloseSnack = () => {
    setOpenSnack(false);
  };

  const performRefresh = useRef(async () => {
    const fns = [
      refreshCompleted.current,
      refreshProgress.current,
      refreshDelayed.current,
      refreshGraph.current,
    ].filter(Boolean);
    await Promise.all(fns.map((fn) => fn()));
  });

  const handleRefreshDashboard = async () => {
    try {
      setIsRefreshing(true);
      setMessage('Refreshing dashboard data...');
      setSeverity('info');
      setOpenSnack(true);

      if (isNewDashboardView && comp_acct) {
        await syncNewDashboardData();
      } else {
        await performRefresh.current();
      }

      setMessage('Dashboard refreshed successfully');
      setSeverity('success');
      setOpenSnack(true);
      setTimeout(() => setOpenSnack(false), 3000);
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      setMessage('Error refreshing dashboard data');
      setSeverity('error');
      setOpenSnack(true);
      setTimeout(() => setOpenSnack(false), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setShowLoading(false);
    }, 1500);
  }, []);

  const [revenueReport, setRevenueReport] = useState(false);
  const [revenueReportDate, setRevenueReportDate] = useState(moment().format('YYYY-MM-DD'));
  const [dateEnd, setDateEnd] = useState(moment().add(1, 'days').format('YYYY-MM-DD'));
  const [revenueReportLoading, setRevenueReportLoading] = useState(false);
  const [makeCsv, setMakeCsv] = useState(false);
  const [emails, setEmails] = useState(() => {
    const userEmail = store.getState().loadz_auth?.AuthReducer?.user?.user_email;
    return userEmail ? [userEmail] : [];
  });

  const _showMessage = (msg, color) => {
    dispatch(
      showMessage({
        message: msg, //text or html
        autoHideDuration: 6000, //ms

        anchorOrigin: {
          vertical: 'top', //top bottom
          horizontal: 'right', //left center right
        },
        variant: color, //success error info warning null
      })
    );
  };

  const generateRvenueReport = async (dateSource = 'ticket_gli') => {
    try {
      const data = {
        revenueReportDate: revenueReportDate,
        dateEnd: dateEnd,
        makeCsv: makeCsv,
        emails: emails,
      };
      ButtonLog({ type: 'dashboard', button_name: 'dashboard_report', info: data });
      if (!revenueReportDate && !dateEnd) {
        return _showMessage('Please select a date', 'error');
      }

      setRevenueReportLoading(true);

      const result = await fetch(`${URLWOCLOUDFLARE}/jobs/daily-revenue-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: `${localStorage.token}`,
          timezone: moment.tz.guess(true),
        },
        body: JSON.stringify({
          startdate: revenueReportDate,
          enddate: dateEnd,
          compacctnum: company.compacctnum,
          csv: makeCsv,
          emails,
          compName: company.compname,
          dateSource: dateSource, // Add date source parameter
        }),
      });

      if (result.status === 500 || result.status === 404) {
        setRevenueReportLoading(false);
        setRevenueReport(false);
        setRevenueReportDate(moment().format('YYYY-MM-DD'));
        setDateEnd(moment().add(1, 'days').format('YYYY-MM-DD'));
        setMakeCsv(false);
        return _showMessage('Error generating report', 'error');
      }

      let start_date = revenueReportDate;
      let end_date = dateEnd;

      if (start_date && end_date) {
        if (moment(start_date).isAfter(end_date)) {
          const temp = end_date;
          end_date = start_date;
          start_date = temp;
        }

        if (moment(end_date).isBefore(start_date)) {
          const temp = end_date;
          end_date = start_date;
          start_date = temp;
        }
      } else {
        if (start_date && !dateEnd) {
          end_date = start_date;
        } else if (!start_date && dateEnd) {
          start_date = end_date;
        }
      }

      let isJson = false;

      if (result.headers.get('content-type').includes('application/json')) {
        try {
          const json = await result.json();

          if (result.status === 200) {
            setRevenueReportLoading(false);
            setRevenueReport(false);
            setRevenueReportDate(moment().format('YYYY-MM-DD'));
            setDateEnd(moment().add(1, 'days').format('YYYY-MM-DD'));
            setMakeCsv(false);
            _showMessage(json.message, 'success');
            isJson = true;
            return;
          }

          setRevenueReportLoading(false);
          setMessage(json.message);
          isJson = true;
        } catch (error) {
          console.log(error);
        }
      }

      if (result.headers.get('content-type') != 'application/json' && isJson === false) {
        let filename = `daily_revenue_report_${company.compname}_${start_date ? moment(start_date).format('MM-DD-YYYY') + '_' : ''
          }${start_date ? 'to_' : ''}${end_date ? moment(end_date).format('MM-DD-YYYY') : ''}`;
        if (makeCsv) {
          filename = `${filename}.csv`;
        } else {
          filename = `${filename}.xlsx`;
        }

        try {
          const blob = await result.blob();
          const file = window.URL.createObjectURL(blob);
          saveAs(file, filename);

          setRevenueReportLoading(false);
          setRevenueReport(false);
          setRevenueReportDate(moment().format('YYYY-MM-DD'));
          setDateEnd(moment().add(1, 'days').format('YYYY-MM-DD'));
          setMakeCsv(false);
        } catch (error) {
          console.log(error);
        }
      }
    } catch (error) {
      console.log(error);
      setRevenueReportLoading(false);
      setRevenueReport(false);
      setRevenueReportDate(moment().format('YYYY-MM-DD'));
      setDateEnd(moment().add(1, 'days').format('YYYY-MM-DD'));
      setMakeCsv(false);
    }
  };

  const [driverEarnings, setDriverEarnings] = useState([]);
  const [driverTimesheets, setDriverTimesheets] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [openQuickDriverEarnings, setOpenQuickDriverEarnings] = useState(false);

  const [earningsLoading, setEarningsLoading] = useState(false);

  const getDriverEarnings = async (driver, compacctnum) => {
    try {
      setEarningsLoading(true);
      const earnings_result = await client.get(`/salary/single-current/${driver}/${compacctnum}`);

      const timesheets_result = await client.post('/salary/get-single-timesheet', {
        comp_acct: compacctnum,
        driver_id: driver,
        startDate: earnings_result.data.driver_breakdowns[0].batches[0].start,
        endDate: earnings_result.data.driver_breakdowns[0].batches[0].end,
      });

      setDriverEarnings(earnings_result.data.driver_breakdowns);
      setDriverTimesheets([timesheets_result.data]);
      setSelectedDriver(driver);
      setOpenQuickDriverEarnings(true);
      setEarningsLoading(false);
    } catch (error) {
      console.log('getDriverEarnings ERROR => ', error);
      setEarningsLoading(false);
      _showMessage('Error getting driver earnings', 'error');
    }
  };

  const handleCloseQuickDriverEarnings = () => {
    setOpenQuickDriverEarnings(false);
    setDriverEarnings([]);
    setDriverTimesheets([]);
    setSelectedDriver;
  };

  const [openTicketPopup, setOpenTicketPopup] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [ticketAuditData, setTicketAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const getTicketAudit = async (ticket) => {
    try {
      setAuditLoading(true);

      const result = await client.post(`ticket/audit`, {
        tk_id: ticket.tk_id,
        compacctnum: ticket.c_id,
      });

      if (result.data.length) {
        setTicketAuditData(result.data);
      } else {
        setTicketAuditData([]);
      }
      setAuditLoading(false);
    } catch (error) {
      setAuditLoading(false);

      setTicketAuditData([]);
      _showMessage('Error', 'error');
    }
  };

  function getTimeDuration(timeData) {
    if (
      timeData &&
      timeData.tk_wait_start &&
      timeData.tk_wait_start.length > 0 &&
      timeData.tk_wait_end &&
      timeData.tk_wait_end.length > 0
    ) {
      try {
        //console.log('WAIT TIMES > ', timeData);
        let total_hours = 0;
        let total_minutes = 0;
        if (timeData.tk_wait_start.length == timeData.tk_wait_end.length) {
          for (let i = 0; i < timeData.tk_wait_start.length; i++) {
            var start_time =
              moment.tz(timeData.tk_wait_start[i], moment.tz.guess(true)).format('HH:mm') ==
                'Invalid date'
                ? moment
                  .tz(
                    `${moment().format('YYYY-MM-DD')} ` + timeData.tk_wait_start[i],
                    moment.tz.guess(true)
                  )
                  .format('HH:mm:ss')
                : moment.tz(timeData.tk_wait_start[i], moment.tz.guess(true)).format('HH:mm:ss');
            var end_time =
              moment.tz(timeData.tk_wait_end[i], moment.tz.guess(true)).format('HH:mm') ==
                'Invalid date'
                ? moment
                  .tz(
                    `${moment().format('YYYY-MM-DD')} ` + timeData.tk_wait_end[i],
                    moment.tz.guess(true)
                  )
                  .format('HH:mm:ss')
                : moment.tz(timeData.tk_wait_end[i], moment.tz.guess(true)).format('HH:mm:ss');
            var time_diff = diff(start_time, end_time);
            time_diff = time_diff.split(':');

            const hours = +time_diff[0];
            const minutes = +time_diff[1];

            if (hours) total_hours = total_hours + hours;
            if (minutes) total_minutes = total_minutes + minutes;
          }
        }
        while (total_minutes >= 60) {
          total_minutes = total_minutes - 60;
          total_hours = total_hours + 1;
        }
        let tkTime = total_hours + ' Hours ' + total_minutes + ' Minutes';
        return tkTime;
      } catch (error) {
        return null;
      }
    } else {
      return null;
    }
  }

  function getTkStartEndTime(startEndTime) {
    if (startEndTime && startEndTime.tk_start && startEndTime.tk_end) {
      try {
        let total_hours = 0;
        let total_minutes = 0;
        for (let i = 0; i < startEndTime.tk_start.length; i++) {
          //console.log('TIMES ', startEndTime.tk_start[i], startEndTime.tk_end[i]);
          const tkStart =
            moment.tz(startEndTime.tk_start[i], moment.tz.guess(true)).format('HH:mm:ss') ==
              'Invalid date'
              ? moment
                .tz(
                  `${moment().format('YYYY-MM-DD')} ` + startEndTime.tk_start[i],
                  moment.tz.guess(true)
                )
                .format('HH:mm:ss')
              : moment.tz(startEndTime.tk_start[i], moment.tz.guess(true)).format('HH:mm:ss');
          const tkEnd =
            moment.tz(startEndTime.tk_end[i], moment.tz.guess(true)).format('HH:mm:ss') ==
              'Invalid date'
              ? moment
                .tz(
                  `${moment().format('YYYY-MM-DD')} ` + startEndTime.tk_end[i],
                  moment.tz.guess(true)
                )
                .format('HH:mm:ss')
              : moment.tz(startEndTime.tk_end[i], moment.tz.guess(true)).format('HH:mm:ss');

          var time_diff = diff(tkStart, tkEnd);

          time_diff = time_diff.split(':');

          const hours = +time_diff[0];
          const minutes = +time_diff[1];
          if (hours) total_hours = total_hours + hours;
          if (minutes) total_minutes = total_minutes + minutes;
        }

        while (total_minutes >= 60) {
          total_minutes = total_minutes - 60;
          total_hours = total_hours + 1;
        }
        let tkTime = total_hours + ' Hours ' + total_minutes + ' Minutes';
        return tkTime;
      } catch (err) {
        //console.log(err.message);
        return null;
      }
    } else {
      return null;
    }
  }

  const getTicketData = async (ticketId) => {
    try {
      const result = await client.post('/jobs/ticket-data', { tk_id: ticketId });

      let key = result.data?.logo;
      if (!key.includes('http')) {
        result.data.logo = `${constants.URLLOCAL}/aws/signed?image=${key}`;
      }

      if (result.data?.tk_status != 3) {
        let duration = getTimeDuration(result.data);
        let tkDuration = getTkStartEndTime(result.data);
        if (duration || tkDuration) {
          result.data.duration_time = duration;
          result.data.tk_start_end_duration = tkDuration;
        }
      }

      /**
       * result.data.newJobDestination
       **/

      setTicketData(result.data);
      setOpenTicketPopup(true);
      getTicketAudit(result.data);
    } catch (error) {
      setTicketData(null);
      setOpenTicketPopup(false);
      _showMessage('Error opening ticket popup', 'error');
    }
  };

  const handleCloseQuickTicketPopup = () => {
    setOpenTicketPopup(false);
    setTicketData(null);
    setTicketAuditData([]);
  };

  if (showLoading) {
    return (
      <div
        style={{ backgroundColor: '#000000' }}
        className={clsx(
          'flex flex-1 flex-col items-center justify-center p-24',
          !showLoading && 'hidden'
        )}
      >
        {showLoading && (
          <>
            <Typography className='text-13 sm:text-20 mb-16' color='textSecondary'>
              {t('LOADING')}
            </Typography>
            <LinearProgress className='w-192 sm:w-320 max-w-full rounded-2' color='secondary' />
          </>
        )}
      </div>
    );
  } else {
    return (
      <div
        className={clsx(
          'flex flex-1 flex-col mobile-p-0 dashboard-table',
          isNewDashboardView ? 'py-0 px-8 sm:px-12' : 'p-24'
        )}
        style={{ backgroundColor: '#000000' }}
      >
        {!showLoading && (
          <>
            <Grid
              container
              spacing={isNewDashboardView ? 0 : 2}
              alignItems='stretch'
              sx={isNewDashboardView ? { flex: 1, minHeight: 0, alignContent: 'flex-start' } : undefined}
            >
              <Snackbar
                sx={{
                  paddingTop: '50px',
                  color: severity,
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={openSnack}
                autoHideDuration={6000}
              >
                <Alert
                  onClick={() => { }}
                  onClose={handleCloseSnack}
                  severity={severity ? severity : 'warning'}
                  sx={{ width: '100%' }}
                >
                  {message}
                </Alert>
              </Snackbar>

              <Grid item xs={12}>
                <div
                  className='flex w-full justify-between align-center'
                  style={{ padding: '0 20px' }}
                >
                  <div className='flex items-center gap-3'>
                    <Typography component='span' className=' h1 font-Regular text-white' align='left'>
                      {compname}
                    </Typography>
                    {isRefreshing && (
                      <Typography
                        component='span'
                        className='text-sm font-Regular text-white animate-pulse'
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: 'rgba(237, 202, 51, 0.2)',
                          padding: '6px 12px',
                          borderRadius: '4px'
                        }}
                      >
                        <RefreshIcon
                          style={{
                            width: '18px',
                            height: '18px',
                            animation: 'spin 1s linear infinite'
                          }}
                        />
                        Loading data...
                      </Typography>
                    )}
                  </div>
                  <div className='flex items-center gap-2'>
                    <FormControlLabel
                      control={
                        <IconButton
                          size='small'
                          onClick={() => setIsNewDashboardView(!isNewDashboardView)}
                          aria-pressed={isNewDashboardView}
                          sx={{
                            color: '#ffc107',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 193, 7, 0.15)',
                            },
                            '& .MuiSvgIcon-root': {
                              color: '#ffc107',
                            },
                          }}
                        >
                          <ReportProblemIcon />
                        </IconButton>
                      }
                      label={
                        <Typography style={{ color: '#000000', fontWeight: 500 }}>
                          {isNewDashboardView ? 'Original Dashboard View' : 'New Dashboard View'}
                        </Typography>
                      }
                      sx={{
                        backgroundColor: '#ffffff',
                        padding: '4px 12px',
                        borderRadius: '8px',
                        margin: 0,
                        '& .MuiFormControlLabel-label': {
                          marginLeft: '8px',
                        },
                      }}
                    />
                    <Tooltip
                      placement='left'
                      title={<h6 style={{ color: 'white' }}>Refresh Dashboard</h6>}
                    >
                      <IconButton
                        onClick={handleRefreshDashboard}
                        disabled={isRefreshing}
                      >
                        <RefreshIcon
                          style={{
                            color: 'white',
                            height: '30px',
                            width: '30px',
                            transform: isRefreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                            transition: 'transform 0.6s linear'
                          }}
                          sx={{
                            '@keyframes spin': {
                              '0%': { transform: 'rotate(0deg)' },
                              '100%': { transform: 'rotate(360deg)' }
                            },
                            animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                    {hasPermission('dashboard-revenue-report') && (
                      <Tooltip
                        placement='left'
                        title={<h6 style={{ color: 'white' }}>{t('DAILY_REVENUE_REPORT')}</h6>}
                      >
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setRevenueReport(true);
                          }}
                        >
                          <EventNoteIcon style={{ color: 'white', height: '30px', width: '30px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
                {isRefreshing && (
                  <div style={{ width: '100%', padding: '0 20px', marginTop: '10px' }}>
                    <LinearProgress
                      color='secondary'
                      sx={{
                        backgroundColor: 'rgba(237, 202, 51, 0.2)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#edca33'
                        }
                      }}
                    />
                  </div>
                )}
              </Grid>

              {isNewDashboardView ? (
                <NewDashboardView 
                  compname={compname} 
                  refreshGraph={refreshGraph}
                  refreshDispatchSection={refreshDispatchSection}
                  comp_acct={comp_acct}
                  progress={progress}
                  completed={completed}
                  progressTickets={progressTickets}
                  completedTickets={completedTickets}
                  getTicketData={getTicketData}
                  getDriverEarnings={getDriverEarnings}
                  setProgress={setProgress}
                  setCompleted={setCompleted}
                  setDelayed={setDelayed}
                  setProgressTickets={setProgressTickets}
                  setCompletedTickets={setCompletedTickets}
                  setDelayedTickets={setDelayedTickets}
                  setQtyProgress={setQtyProgress}
                  setQtyCompleted={setQtyCompleted}
                  setQtyDelayed={setQtyDelayed}
                  refreshProgress={refreshProgress}
                  refreshCompleted={refreshCompleted}
                  refreshDelayed={refreshDelayed}
                  allTablesData={allTablesData}
                  isDelayed={response.isDelayed}
                />
              ) : (
                <>
                <Grid item xs={12}>
                <div className='flex w-full mobile-flex-col flex-col md-screens lg:flex-row'>
                  <div className='w-full lg:w-3/5  mobile-width-full m-d-screenss'>
                    <Card
                      sx={{}}
                      className='rounded-none'
                      style={{
                        backgroundColor: '#000000',
                      }}
                    >
                      <CardHeader title='' subheader='' />
                      <CardContent className='dashboardGraph'>
                        <ChartRange refreshGraph={refreshGraph} compacct={comp_acct} />
                      </CardContent>
                    </Card>
                  </div>
                  <div className='flex w-full lg:w-3/5 mobile-width-full'>
                    <div className='w-full '>
                      <Card
                        className='rounded-none '
                        style={{
                          backgroundColor: '#000000',
                        }}
                      >
                        <CardHeader title='' subheader='' />
                        <CardContent className='dashboardCounters' sx={{}}>
                          <Counters
                            counters={{ progress, delayed, completed }}
                            goTo={goTo}
                            progressTickets={progressTickets}
                            delayedTickets={delayedTickets}
                            completedTickets={completedTickets}
                          />
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              </Grid>

                <Grid item xs={12}>
                  <Card sx={{}} style={{ backgroundColor: '#000000' }}>
                    <CardHeader
                      title={t('IN_PROGRESS')}
                      className='text-white'
                      subheader=''
                      id='tableProgress'
                    />

                    <CardContent>
                      <JobTable
                      getTicketData={getTicketData}
                      getDriverEarnings={getDriverEarnings}
                      setJobCount={setProgress}
                      refreshTable={refreshProgress}
                      ticketCount={setProgressTickets}
                      setQuantity={setQtyProgress}
                      compacct={comp_acct}
                      jobType={jobTypes.progress}
                      jobStatus='Progress'
                      initialData={allTablesData?.progress}
                      waitForInitialData={!allTablesData}
                    />
                  </CardContent>
                  <CardActions disableSpacing>
                    <ExpandMore
                      className='moress'
                      expand={expandedProgress}
                      onClick={handleExpandClickProgress}
                      aria-expanded={expandedProgress}
                      aria-label='show more'
                    >
                      <Tooltip title={!expandedProgress ? 'Show Chart' : 'Hide Chart'}>
                        <ExpandMoreIcon />
                      </Tooltip>
                    </ExpandMore>
                  </CardActions>
                  <Collapse in={expandedProgress} timeout='auto' unmountOnExit>
                    <CardContent>
                      <ChartProgress qtyProgress={qtyProgress} />
                    </CardContent>
                  </Collapse>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{}} style={{ backgroundColor: '#000000' }}>
                  <CardHeader
                    title={t('COMPLETED_TODAY')}
                    className='text-white'
                    subheader=''
                    id='tableCompleted'
                  />

                  <CardContent>
                    <JobTable
                      getTicketData={getTicketData}
                      getDriverEarnings={getDriverEarnings}
                      setJobCount={setCompleted}
                      refreshTable={refreshCompleted}
                      ticketCount={setCompletedTickets}
                      setQuantity={setQtyCompleted}
                      compacct={comp_acct}
                      jobType={jobTypes.completed}
                      jobStatus='Completed'
                      initialData={allTablesData?.completed}
                      waitForInitialData={!allTablesData}
                    />
                  </CardContent>
                  <CardActions disableSpacing>
                    <ExpandMore
                      expand={expandedCompleted}
                      onClick={handleExpandClickCompleted}
                      aria-expanded={expandedCompleted}
                      aria-label='show more'
                    >
                      <Tooltip title={!expandedCompleted ? 'Show Chart' : 'Hide Chart'}>
                        <ExpandMoreIcon />
                      </Tooltip>
                    </ExpandMore>
                  </CardActions>
                  <Collapse in={expandedCompleted} timeout='auto' unmountOnExit>
                    <CardContent>
                      <ChartCompleted qtyCompleted={qtyCompleted} />
                    </CardContent>
                  </Collapse>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{}} style={{ backgroundColor: '#000000' }}>
                  <CardHeader title={t('DELAYED')} className='text-white' id='tableDelayed' />

                  <CardContent>
                    <JobTable
                      getTicketData={getTicketData}
                      getDriverEarnings={getDriverEarnings}
                      isDelayed={response.isDelayed}
                      setJobCount={setDelayed}
                      refreshTable={refreshDelayed}
                      ticketCount={setDelayedTickets}
                      setQuantity={setQtyDelayed}
                      compacct={comp_acct}
                      jobType={jobTypes.delay}
                      jobStatus='Delayed'
                      initialData={allTablesData?.delayed}
                      waitForInitialData={!allTablesData}
                    />
                  </CardContent>
                  <CardActions disableSpacing>
                    <ExpandMore
                      expand={expandedDelayed}
                      onClick={handleExpandClickDelayed}
                      aria-expanded={expandedDelayed}
                      aria-label='show more'
                    >
                      <Tooltip title={!expandedDelayed ? 'Show Chart' : 'Hide Chart'}>
                        <ExpandMoreIcon />
                      </Tooltip>
                    </ExpandMore>
                  </CardActions>
                  <Collapse in={expandedDelayed} timeout='auto' unmountOnExit>
                    <CardContent>
                      <ChartDelayed qtyDelayed={qtyDelayed} />
                    </CardContent>
                  </Collapse>
                </Card>
              </Grid>
                </>
              )}
            </Grid>

            <RevenueReport
              open={revenueReport}
              setOpen={setRevenueReport}
              date={revenueReportDate}
              setDate={setRevenueReportDate}
              generateReport={generateRvenueReport}
              loading={revenueReportLoading}
              setMakeCsv={setMakeCsv}
              makeCsv={makeCsv}
              dateEnd={dateEnd}
              setDateEnd={setDateEnd}
              setSendEmails={setEmails}
              SendEmails={emails}
              message={message}
            />

            {earningsLoading ? (
              <Dialog open={earningsLoading}>
                <FuseLoading />
              </Dialog>
            ) : null}

            <QuickDriverEarnings
              open={openQuickDriverEarnings}
              handleClose={handleCloseQuickDriverEarnings}
              driverEarnings={driverEarnings}
              driverTimesheets={driverTimesheets}
              selectedDriver={selectedDriver}
            />

            <TicketPopup
              getTicketAudit={getTicketAudit}
              open={openTicketPopup}
              handleclose={handleCloseQuickTicketPopup}
              data={ticketData}
              logo={ticketData?.logo}
              newLoc={ticketData?.newJobDestination}
              auditLoading={auditLoading}
              ticketAuditData={ticketAuditData}
            />
          </>
        )}
      </div>
    );
  }
}

Dashboard.propTypes = {
  delay: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
};

Dashboard.defaultProps = {
  delay: false,
};
injectReducer('loadz_auth', AuthReducer);

const mapStateToProps = (state) => {
  return {
    company: state?.loadz_auth?.AuthReducer?.user?.company,
    user: state?.loadz_auth?.AuthReducer?.user,
  };
};

export default connect(mapStateToProps, null)(memo(Dashboard));
